import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Head from 'components/Head';

import { media } from 'context';
import { FullWidthBackground, Layout, Link, PillButton, Text } from 'ds';
import { BLUE_50_HEX } from 'shared/colors';
import { useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';
import { TENANT_ADMIN_INTEGRATIONS_PATH } from 'routes';
import { getTenancySlackInstallation } from 'ux/Tenant/TenantAdmin/Integrations/requests';

import { ReactComponent as SlackInstalledHeroDesktopSvg } from './SlackInstalledHeroDesktop.svg';
import { ReactComponent as SlackInstalledHeroMobileSvg } from './SlackInstalledHeroMobile.svg';

export type SlackIntegrationInstalledLocationState = {
  appHomeDeepLink?: string | null;
  missingScopes?: string[];
};

const BANNER_IMAGE_URL = 'https://assets.codi.com/Codi%20AI%20Slack%20App/cover_pt-DmMI1m.jpg';
const BANNER_BACKGROUND = `url('${BANNER_IMAGE_URL}') center/cover no-repeat`;

const STEP_1_IMAGE_URL = 'https://assets.codi.com/Codi%20AI%20Slack%20App/1%20gradient_VyEjSOEUy.png';
const STEP_2_IMAGE_URL = 'https://assets.codi.com/Codi%20AI%20Slack%20App/2%20gradient_mOiWb8ly3.png';

/** Matches `has-shadow-soft` in `Button.scss` (soft hero illustration depth). */
const HERO_SLACK_ILLUSTRATION_DROP_SHADOW = 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.06))';

const SlackIntegrationInstalled: React.FC = () => {
  const { xs, sm, isMobile } = useContext(media);
  const paddingX = xs ? 24 : sm ? 40 : 80;
  const location = useLocation<SlackIntegrationInstalledLocationState>();
  const tenancyId = useAppSelector(selectTenancyId);
  const stateDeepLink = location.state?.appHomeDeepLink;
  const missingScopes = location.state?.missingScopes ?? [];
  const hasMissingScopes = missingScopes.length > 0;
  const [fetchedDeepLink, setFetchedDeepLink] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (tenancyId == null) {
      return;
    }
    if (typeof stateDeepLink === 'string' && stateDeepLink.length > 0) {
      return;
    }

    let cancelled = false;

    void getTenancySlackInstallation(tenancyId)
      .then(({ data }) => {
        if (!cancelled) {
          setFetchedDeepLink(data.app_home_deep_link ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFetchedDeepLink(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [tenancyId, stateDeepLink]);

  const openInSlackHref = (() => {
    if (typeof stateDeepLink === 'string' && stateDeepLink.length > 0) {
      return stateDeepLink;
    }
    if (fetchedDeepLink === undefined) {
      return null;
    }
    return fetchedDeepLink;
  })();

  return (
    <Layout color="blue-50" width="100%" __style={{ backgroundColor: BLUE_50_HEX }}>
      <Head title="Slack integration installed" />
      <FullWidthBackground
        background={BANNER_BACKGROUND}
        containerProps={{ paddingX }}
        {...(isMobile ? { paddingTop: 48, paddingBottom: 48 } : { paddingY: 80 })}
      >
        <Layout maxWidth={1120} width="100%" __style={{ marginLeft: 'auto', marginRight: 'auto' }}>
          <Layout
            direction={isMobile ? 'column' : 'row'}
            align={isMobile ? 'stretch' : 'center'}
            justify={isMobile ? 'flex-start' : 'space-between'}
            gap={24}
            wrap={false}
            width="100%"
          >
            <Layout direction="column" gap={20} align={isMobile ? 'center' : 'flex-start'} minWidth={0}>
              <Text size="body-xl" color="white" tag="h1" {...(isMobile ? { align: 'center' as const } : {})}>
                Slack integration successfully installed.
              </Text>
              {openInSlackHref ? (
                <Layout
                  display="inline-flex"
                  minWidth={200}
                  {...(isMobile ? { align: 'center' as const, justify: 'center' as const } : {})}
                >
                  <PillButton
                    type="primaryInverse"
                    size="lg"
                    fullWidth
                    hasShadowSoft
                    href={openInSlackHref}
                    target="_self"
                    text="View in Slack"
                    icon="externalLink"
                  />
                </Layout>
              ) : null}
              <Link
                href={TENANT_ADMIN_INTEGRATIONS_PATH}
                color="white"
                underline={false}
                opacity={false}
                display="inline-flex"
                size="link-md"
                semibold={false}
                context="slack_integration_installed_back"
              >
                ← Back to integrations
              </Link>
            </Layout>
            <Layout flexShrink={0} justify="center" align="center">
              <Layout
                width={isMobile ? 342 : 542}
                height={isMobile ? 84 : 118}
                flexShrink={0}
                __style={{ filter: HERO_SLACK_ILLUSTRATION_DROP_SHADOW }}
              >
                {isMobile ? (
                  <SlackInstalledHeroMobileSvg width="100%" height="100%" />
                ) : (
                  <SlackInstalledHeroDesktopSvg width="100%" height="100%" />
                )}
              </Layout>
            </Layout>
          </Layout>
        </Layout>
      </FullWidthBackground>

      {hasMissingScopes ? (
        <Layout paddingTop={isMobile ? 24 : 40} paddingX={paddingX}>
          <Layout
            maxWidth={1120}
            width="100%"
            color="gold-100"
            borderRadius={12}
            padding={isMobile ? 16 : 24}
            __style={{ marginLeft: 'auto', marginRight: 'auto' }}
          >
            <Layout direction="column" gap={8}>
              <Text size="body-md" color="gold-800" semibold>
                Codi needs updated permissions
              </Text>
              <Text size="body-md" color="gray-700">
                Your Slack installation is missing permissions needed for channel @mentions. Please reinstall by
                clicking the button below — your existing conversations and settings will be preserved.
              </Text>
              <Link
                href={TENANT_ADMIN_INTEGRATIONS_PATH}
                color="blue-500"
                underline
                opacity={false}
                display="inline-flex"
                size="link-md"
                semibold
                context="slack_installed_missing_scopes_reinstall"
              >
                Go to Integrations to reinstall
              </Link>
            </Layout>
          </Layout>
        </Layout>
      ) : null}

      <Layout paddingTop={isMobile ? 48 : 80} paddingBottom={isMobile ? 48 : 80} paddingX={paddingX}>
        <Layout
          maxWidth={1120}
          width="100%"
          color="blue-100"
          borderRadius={16}
          padding={isMobile ? 24 : 48}
          __style={{ marginLeft: 'auto', marginRight: 'auto' }}
        >
          <Layout marginBottom={32} {...(isMobile ? { align: 'center' as const, justify: 'center' as const } : {})}>
            <Text
              size="headline-md"
              color="gray-900"
              tag="h2"
              semibold
              {...(isMobile ? { align: 'center' as const } : {})}
            >
              Finish setting up your Slack integration
            </Text>
          </Layout>
          <Layout direction="column" gap={24} {...(isMobile ? { align: 'center' as const } : {})}>
            <Layout
              direction={isMobile ? 'column' : 'row'}
              gap={isMobile ? 8 : 16}
              align={isMobile ? 'center' : 'flex-start'}
              {...(isMobile ? { justify: 'center' as const } : {})}
            >
              <Layout flexShrink={0} width={32} height={32} justify="center" align="center">
                <img src={STEP_1_IMAGE_URL} alt="" width={32} height={32} />
              </Layout>
              <Layout direction="column" gap={4} {...(isMobile ? { align: 'center' as const } : { paddingTop: 4 })}>
                <Layout {...(isMobile ? { justify: 'center' as const } : {})}>
                  <Text size="body-md" color="gray-900" semibold {...(isMobile ? { align: 'center' as const } : {})}>
                    Add easy access
                  </Text>
                </Layout>
                <Text size="body-md" color="gray-700" {...(isMobile ? { align: 'center' as const } : {})}>
                  Click the Add Agent button (robot + icon) in the top right of Slack and choose Codi AI Office
                  Assistant.
                </Text>
              </Layout>
            </Layout>
            <Layout
              direction={isMobile ? 'column' : 'row'}
              gap={isMobile ? 8 : 16}
              align={isMobile ? 'center' : 'flex-start'}
              {...(isMobile ? { justify: 'center' as const } : {})}
            >
              <Layout flexShrink={0} width={32} height={32} justify="center" align="center">
                <img src={STEP_2_IMAGE_URL} alt="" width={32} height={32} />
              </Layout>
              <Layout direction="column" gap={4} {...(isMobile ? { align: 'center' as const } : { paddingTop: 4 })}>
                <Layout {...(isMobile ? { justify: 'center' as const } : {})}>
                  <Text size="body-md" color="gray-900" semibold {...(isMobile ? { align: 'center' as const } : {})}>
                    Ask about your office
                  </Text>
                </Layout>
                <Text size="body-md" color="gray-700" {...(isMobile ? { align: 'center' as const } : {})}>
                  Click the Codi AI Office Assistant icon in the top right to ask questions or request help with your
                  office.
                </Text>
              </Layout>
            </Layout>
          </Layout>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default SlackIntegrationInstalled;
